import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { has, isEqual } from 'lodash';
import { useParams, useLocation } from 'react-router-dom';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';

import Dialog from 'components/ui/Dialog';
import Form from 'components/ui/Form';
import {
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
  FormControl,
} from '@material-ui/core';
import {
  FormSectionTitle,
  GridContainer,
  StyledField,
  StyledHelperText,
} from 'styles/form';
import {
  createAccountManager,
  deleteAccountManager,
  showAccountManager,
  updateAccountManager,
} from 'api/account-manager';
import { useSnackBar } from 'context/SnackBarContext';
import { initialValues, validationSchema } from '../account-manager.config';

const AccountManager = ({
  modalOpen,
  handleModalClose,
  blockRouteChange,
  setBlockRouteChange,
}) => {
  const { id } = useParams();
  const { pathname } = useLocation();
  const routeDepth = pathname.split('/').length - 1;

  const [accountManager, setAccountManager] = useState();
  const { setMessage, setOpenSnackBar, setSeverity } = useSnackBar();

  useEffect(async () => {
    if (id && routeDepth <= 2) {
      const { data } = await showAccountManager(id);
      setAccountManager(data);
    }
  }, []);

  const handleSave = async (values) => {
    if (accountManager) {
      try {
        await updateAccountManager(id, values);
        setMessage('Saved changes');
        setSeverity('success');
      } catch (error) {
        console.error(error);
        setMessage('Something went wrong. Failed to save changes.');
        setSeverity('error');
      }
    } else {
      try {
        await createAccountManager(values);
        setMessage('Account manager created');
        setSeverity('success');
      } catch (error) {
        console.error(error);
        setMessage('Something went wrong. Failed to create account manager.');
        setSeverity('error');
      }
    }

    setOpenSnackBar(true);
    handleModalClose(true);
  };

  const handleDelete = async () => {
    try {
      await deleteAccountManager(id);
      setMessage('Admin deleted');
      setSeverity('success');
    } catch (error) {
      console.error(error);
      setMessage('Something went wrong. Failed to delete account manager.');
      setSeverity('error');
    }

    setOpenSnackBar(true);
    handleModalClose(true);
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <Formik
        initialValues={initialValues(accountManager)}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSave}
      >
        {({ values, errors, handleChange, submitForm, touched }) => {
          const { firstName, lastName, email, rate } = values;
          const initValues = initialValues(accountManager);

          const unsavedChange = !isEqual(
            {
              ...values,
              rate: Number(rate),
            },
            { ...initValues, rate: Number(initValues.rate) },
          );

          if (unsavedChange) {
            setBlockRouteChange(true);
          } else {
            setBlockRouteChange(false);
          }

          return (
            <Dialog
              open={modalOpen}
              handleClose={handleModalClose}
              submitForm={submitForm}
              typeText="Account Manager"
              handleDelete={handleDelete}
              blockRouteChange={blockRouteChange}
            >
              <FormSectionTitle>Account Manager</FormSectionTitle>
              <Form fullWidth>
                <GridContainer>
                  <TextField
                    color="primary"
                    error={touched.firstName && has(errors, 'firstName')}
                    helperText={touched.firstName && errors.firstName}
                    label="First Name"
                    name="firstName"
                    onChange={handleChange}
                    value={firstName}
                    variant="outlined"
                    required
                  />

                  <TextField
                    color="primary"
                    error={touched.lastName && has(errors, 'lastName')}
                    fullWidth
                    helperText={touched.lastName && errors.lastName}
                    label="Last Name"
                    name="lastName"
                    onChange={handleChange}
                    value={lastName}
                    variant="outlined"
                    required
                  />

                  <TextField
                    color="primary"
                    error={touched.email && has(errors, 'email')}
                    fullWidth
                    helperText={touched.email && errors.email}
                    label="Email"
                    name="email"
                    onChange={handleChange}
                    value={email}
                    type="email"
                    variant="outlined"
                    required
                  />

                  <FormControl fullWidth variant="outlined">
                    <StyledField variant="outlined">
                      <InputLabel htmlFor="outlined-adornment-amount" required>
                        Rate (%/hr)
                      </InputLabel>
                      <OutlinedInput
                        onChange={handleChange}
                        endAdornment={
                          <InputAdornment position="start">%</InputAdornment>
                        }
                        labelWidth={85}
                        error={touched.rate && has(errors, 'rate')}
                        helperText={touched.rate && errors.rate}
                        name="rate"
                        value={rate}
                      />
                    </StyledField>
                    {errors.rate && touched.rate && (
                      <StyledHelperText error={has(errors, 'rate')}>
                        field is required
                      </StyledHelperText>
                    )}
                  </FormControl>
                </GridContainer>
              </Form>
            </Dialog>
          );
        }}
      </Formik>
    </MuiPickersUtilsProvider>
  );
};

export default AccountManager;
